/**
 * Autopilot Service - Frontend API Client
 *
 * Communicates with the backend Autopilot endpoints.
 */

import { apiClient } from './apiClient';

// =============================================================================
// Types (mirroring backend types)
// =============================================================================

export type SignalType =
  // Phase 1 (MVP) types
  | 'content_decay'
  | 'missing_entities'
  | 'competitor_new_page'
  | 'ranking_drop'
  | 'thin_content'
  | 'stale_content'
  | 'keyword_gap'
  | 'internal_linking_gap'
  // Phase 2: Content Quality signals
  | 'low_readability'
  | 'keyword_cannibalization'
  // Phase 2: Competitor Intelligence signals
  | 'competitor_outranking'
  | 'competitor_feature_gap'
  // Phase 3: E-E-A-T signals
  | 'missing_author_info'
  | 'missing_citations'
  | 'low_trust_score'
  // Phase 3: Schema signals
  | 'missing_schema'
  // Phase 4: Technical SEO signals
  | 'poor_meta_description'
  | 'weak_title_tag'
  | 'missing_canonical'
  // Phase 4: Conversion signals
  | 'comparison_opportunity'
  | 'cta_missing'
  | 'video_opportunity';

export type SignalSeverity = 'low' | 'medium' | 'high' | 'critical';
export type SignalSource = 'knowledge_graph' | 'competitors' | 'keywords' | 'gsc' | 'crawl' | 'analytics';
export type SignalStatus = 'pending' | 'converted' | 'ignored';

export type ActionType =
  // Phase 1 (MVP) types
  | 'create_new_page'
  | 'update_section'
  | 'add_internal_links'
  | 'add_faq_schema'
  | 'add_missing_entities'
  | 'refresh_content'
  // Phase 2: Content Quality actions
  | 'improve_readability'
  // Phase 3: E-E-A-T actions
  | 'add_author_bio'
  | 'add_citations'
  | 'add_expert_quotes'
  | 'add_trust_signals'
  // Phase 3: Schema actions
  | 'add_howto_schema'
  | 'add_product_schema'
  | 'add_article_schema'
  | 'add_breadcrumb_schema'
  | 'add_local_business_schema'
  // Phase 4: Conversion actions
  | 'add_comparison_table'
  | 'add_cta_section'
  | 'add_data_visualization'
  | 'add_video_content'
  // Phase 4: Technical SEO actions
  | 'fix_meta_description'
  | 'optimize_title_tag'
  | 'add_canonical_url';

export type ActionStatus =
  | 'ready'
  | 'generated'
  | 'approved'
  | 'ignored'
  | 'deployed' // legacy read-alias of 'delivered'
  // Phase 1 executor lifecycle
  | 'executing' // approve accepted; artefact generation in flight
  | 'delivered' // artefact generated, validated, stored
  | 'failed'; // honest generation failure; reason surfaced in the Delivered panel
/**
 * The 6 schema ActionTypes the Phase 1 executor delivers (mirror of the backend
 * SCHEMA_ACTION_TYPES). Used by the UI to tell when an approval kicks the executor
 * so the user can be shown the Delivered surface (Phase 1.5 Wave 4).
 */
export const SCHEMA_ACTION_TYPES: ReadonlySet<ActionType> = new Set<ActionType>([
  'add_faq_schema',
  'add_howto_schema',
  'add_product_schema',
  'add_article_schema',
  'add_breadcrumb_schema',
  'add_local_business_schema',
]);

export const isSchemaActionType = (t: string): boolean => SCHEMA_ACTION_TYPES.has(t as ActionType);

export type RunStatus = 'running' | 'completed' | 'failed';
export type AutopilotMode = 'growth' | 'defensive' | 'authority' | 'conversion' | 'technical' | 'balanced';
export type ScheduleFrequency = 'daily' | 'weekly' | 'off';

export interface KeywordSuggestion {
  id: string;
  project_id: string;
  keyword: string;
  source: string;
  metrics: Record<string, unknown>;
  opportunity_score: number;
  status: 'suggested' | 'added' | 'dismissed';
  created_at: string;
  updated_at: string;
}

export interface KeywordSuggestionsResponse {
  suggestions: KeywordSuggestion[];
  total: number;
}

export interface AutopilotSettings {
  mode: AutopilotMode;
  auto_suggest_keywords: boolean;
  auto_add_keywords: boolean;
  auto_run_schedule?: ScheduleFrequency;
  schedule_time?: string;
  schedule_day?: number;
  notification_preferences: {
    email: boolean;
    in_app: boolean;
    on_new_signals: boolean;
    on_run_complete: boolean;
  };
}

export interface AutopilotSignal {
  id: string;
  project_id: string;
  run_id?: string;
  type: SignalType;
  severity: SignalSeverity;
  source: SignalSource;
  status: SignalStatus;
  score: number;
  payload: {
    title: string;
    description: string;
    affected_url?: string;
    affected_keyword?: string;
    detected_value?: string | number;
    threshold?: string | number;
    metadata?: Record<string, unknown>;
  };
  detected_at: string;
  created_at: string;
  updated_at: string;
}

export interface AutopilotAction {
  id: string;
  project_id: string;
  signal_id?: string;
  run_id?: string;
  action_type: ActionType;
  status: ActionStatus;
  priority_score: number;
  impact_score: number;
  urgency_score: number;
  confidence_score: number;
  effort_score: number;
  payload: {
    title: string;
    description: string;
    why_it_matters: string;
    suggested_fix: string;
    target_url?: string;
    target_keyword?: string;
    content_type?: string;
    html_preview?: string;
    metadata?: Record<string, unknown>;
  };
  created_at: string;
  updated_at: string;
  approved_at?: string;
  approved_by?: string;
}

export interface AutopilotRun {
  id: string;
  project_id: string;
  triggered_by: 'user' | 'scheduled' | 'system';
  status: RunStatus;
  started_at: string;
  completed_at?: string;
  summary: {
    signals_detected: number;
    actions_generated: number;
    actions_approved: number;
    actions_ignored: number;
    duration_ms?: number;
    error_message?: string;
  };
  created_at: string;
}

export interface AutopilotInsightsResponse {
  signals: AutopilotSignal[];
  actions: AutopilotAction[];
  summary: {
    total_signals: number;
    total_actions: number;
    high_priority_count: number;
    opportunity_clusters: number;
    score_latest_run?: number; // 0-100, computed from latest run's signals/actions (all statuses)
  };
  last_run?: AutopilotRun;
  autopilot_score: number;
}

export interface AutopilotRefreshResponse {
  run_id: string;
  status: RunStatus;
  signals_detected: number;
  actions_generated: number;
  keywords_suggested?: number;
  keywords_auto_added?: number;
  message: string;
}

export interface AutopilotApproveResponse {
  action_id: string;
  status: ActionStatus;
  message: string;
  deep_link?: string;
}

export interface AutopilotHistoryResponse {
  runs: AutopilotRun[];
  actions: AutopilotAction[];
  total_runs: number;
  total_actions: number;
}

// =============================================================================
// Artefact Types (Phase 1 executor output — Delivered panel)
// =============================================================================

export type ArtifactKind = 'schema_snippet' | 'content_draft';

export interface SchemaArtifactValidation {
  valid: boolean;
  score: number;
  type: string;
}

export interface SchemaSnippetArtifactContent {
  schema_type: string;
  action_type: ActionType;
  jsonld: Record<string, unknown>;
  jsonld_script: string;
  validation: SchemaArtifactValidation;
  generated_from: string[];
}

export interface AutopilotArtifact {
  id: string;
  action_id: string;
  project_id: string;
  kind: ArtifactKind;
  content: SchemaSnippetArtifactContent | Record<string, unknown>;
  content_studio_draft_id?: string | null;
  created_at: string;
}

export interface DeliveredActionItem {
  action: AutopilotAction;
  artifact: AutopilotArtifact | null;
  failure_reason?: string;
}

export interface AutopilotArtifactsResponse {
  items: DeliveredActionItem[];
  delivered_count: number;
  failed_count: number;
}

// =============================================================================
// Insight Layer Types
// =============================================================================

export type InsightStatus = 'active' | 'dismissed' | 'converted' | 'expired';

export type InsightSourceType =
  | 'competitor_analysis'
  | 'knowledge_graph'
  | 'keywords'
  | 'gsc'
  | 'crawl'
  | 'analytics';

export type ContentType =
  | 'blog_post'
  | 'service_page'
  | 'landing_page'
  | 'faq_page'
  | 'case_study'
  | 'resource_guide'
  | 'comparison_page'
  | 'product_page'
  | 'how_to_guide';

export interface ContentIdea {
  id: string;
  title: string;
  content_type: ContentType;
  keywords: string[];
  description?: string;
  outline?: string[];
  talking_points?: string[];
  competitor_reference?: string;
  search_volume?: number;
  difficulty?: number;
  priority?: number;
  source: 'pages_to_create' | 'blog_ideas' | 'quick_wins' | 'gap_analysis' | 'manual';
}

export interface AutopilotInsight {
  id: string;
  project_id: string;
  signal_id?: string;
  run_id?: string;
  headline: string;
  explanation: string;
  opportunity_summary?: string;
  source_type: InsightSourceType;
  source_id?: string;
  source_metadata: {
    history_id?: string;
    tab?: string;
    competitor_domain?: string;
    analysis_date?: string;
    [key: string]: unknown;
  };
  content_ideas: ContentIdea[];
  priority_score: number;
  impact_estimate?: string;
  status: InsightStatus;
  created_at: string;
  updated_at: string;
}

export interface HumanInsightsResponse {
  insights: AutopilotInsight[];
  total: number;
}

export interface ContentPrefill {
  id: string;
  project_id: string;
  insight_id?: string;
  action_id?: string;
  title?: string;
  content_type?: ContentType;
  keywords: string[];
  outline: string[];
  talking_points: string[];
  competitor_reference?: string;
  source_context: Record<string, unknown>;
  expires_at: string;
  used_at?: string;
  created_at: string;
}

export interface CreatePrefillRequest {
  project_id: string;
  insight_id?: string;
  action_id?: string;
  content_idea: ContentIdea;
  source_context?: Record<string, unknown>;
}

export interface CreatePrefillResponse {
  id: string;
  expires_at: string;
}

// =============================================================================
// Readiness Types (New User Onboarding)
// =============================================================================

export interface ReadinessComponent {
  ready: boolean;
  count?: number;
  message: string;
  whyItMatters: string;
  action?: {
    label: string;
    path: string;
  };
}

export interface ReadinessNextStep {
  label: string;
  description: string;
  path: string;
  priority: number;
}

export interface AutopilotReadiness {
  score: number;
  previousScore?: number;
  scoreDelta?: number;
  status: 'ready' | 'partial' | 'needs_setup';
  components: {
    siteCrawl: ReadinessComponent;
    keywords: ReadinessComponent;
    competitors: ReadinessComponent;
    knowledgeGraph: ReadinessComponent;
    gsc: ReadinessComponent;
  };
  canRun: boolean;
  nextStep: ReadinessNextStep;
  recommendations: string[];
}

// =============================================================================
// API Response Wrapper
// =============================================================================

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp?: string;
}

// =============================================================================
// Service Class
// =============================================================================

class AutopilotService {
  private basePath = 'autopilot';

  /**
   * Get Autopilot readiness status for a project
   * Used to check prerequisites and guide new users through setup
   */
  async getReadiness(projectId: string): Promise<ApiResponse<AutopilotReadiness>> {
    try {
      const response = await apiClient.get<ApiResponse<AutopilotReadiness>>(
        `${this.basePath}/readiness/${projectId}`,
        { cache: false }
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to get readiness:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch readiness status',
      };
    }
  }

  /**
   * Get insights for a project (signals, actions, summary)
   */
  async getInsights(projectId: string, opts?: { suppressUpgradeToast?: boolean }): Promise<ApiResponse<AutopilotInsightsResponse>> {
    try {
      const response = await apiClient.get<ApiResponse<AutopilotInsightsResponse>>(
        `${this.basePath}/insights/${projectId}`,
        { cache: false, ...(opts?.suppressUpgradeToast ? { suppressUpgradeToast: true } : {}) }
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to get insights:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch insights',
      };
    }
  }

  /**
   * Trigger a refresh/scan for new signals
   */
  async refreshInsights(
    projectId: string,
    triggeredBy: 'user' | 'scheduled' | 'system' = 'user'
  ): Promise<ApiResponse<AutopilotRefreshResponse>> {
    try {
      const response = await apiClient.post<ApiResponse<AutopilotRefreshResponse>>(
        `${this.basePath}/insights/${projectId}/refresh`,
        { triggeredBy }
      );

      // Invalidate all autopilot caches after refresh so new data is fetched
      if (response.success) {
        apiClient.invalidateCacheByPattern('autopilot');
      }

      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to refresh insights:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to refresh insights',
      };
    }
  }

  /**
   * Generate actions from signals
   */
  async generateActions(
    projectId: string,
    signalIds?: string[]
  ): Promise<ApiResponse<{ actions: AutopilotAction[]; generated_count: number; message: string }>> {
    try {
      const response = await apiClient.post<ApiResponse<{ actions: AutopilotAction[]; generated_count: number; message: string }>>(
        `${this.basePath}/actions/generate`,
        { projectId, signalIds }
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to generate actions:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to generate actions',
      };
    }
  }

  /**
   * Approve or ignore an action
   */
  async approveAction(
    actionId: string,
    decision: 'approve' | 'ignore'
  ): Promise<ApiResponse<AutopilotApproveResponse>> {
    try {
      const response = await apiClient.post<ApiResponse<AutopilotApproveResponse>>(
        `${this.basePath}/actions/${actionId}/approve`,
        { decision }
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to approve action:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to process action',
      };
    }
  }

  /**
   * Get delivered/failed schema artefacts for the Delivered panel (Phase 1).
   * Read-only: returns delivered actions joined to their artefact, plus honest
   * failures (artefact null + failure_reason).
   */
  async getArtifacts(projectId: string, opts?: { suppressUpgradeToast?: boolean }): Promise<ApiResponse<AutopilotArtifactsResponse>> {
    try {
      const response = await apiClient.get<ApiResponse<AutopilotArtifactsResponse>>(
        `${this.basePath}/artifacts/${projectId}`,
        { cache: false, ...(opts?.suppressUpgradeToast ? { suppressUpgradeToast: true } : {}) }
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to get artifacts:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch artefacts',
      };
    }
  }

  /**
   * Get action and run history for a project
   */
  async getHistory(projectId: string, limit = 50, opts?: { suppressUpgradeToast?: boolean }): Promise<ApiResponse<AutopilotHistoryResponse>> {
    try {
      const response = await apiClient.get<ApiResponse<AutopilotHistoryResponse>>(
        `${this.basePath}/actions/${projectId}/history?limit=${limit}`,
        { cache: false, ...(opts?.suppressUpgradeToast ? { suppressUpgradeToast: true } : {}) }
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to get history:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch history',
      };
    }
  }

  /**
   * Get a single action by ID
   */
  async getAction(actionId: string): Promise<ApiResponse<AutopilotAction>> {
    try {
      const response = await apiClient.get<ApiResponse<AutopilotAction>>(
        `${this.basePath}/actions/${actionId}`
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to get action:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch action',
      };
    }
  }

  /**
   * Get a single signal by ID
   */
  async getSignal(signalId: string): Promise<ApiResponse<AutopilotSignal>> {
    try {
      const response = await apiClient.get<ApiResponse<AutopilotSignal>>(
        `${this.basePath}/signals/${signalId}`
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to get signal:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch signal',
      };
    }
  }

  /**
   * Get autopilot settings for a project
   */
  async getSettings(projectId: string): Promise<ApiResponse<AutopilotSettings>> {
    try {
      const response = await apiClient.get<ApiResponse<AutopilotSettings>>(
        `${this.basePath}/settings/${projectId}`
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to get settings:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch settings',
      };
    }
  }

  /**
   * Save autopilot settings for a project
   */
  async saveSettings(projectId: string, settings: Partial<AutopilotSettings>): Promise<ApiResponse<AutopilotSettings>> {
    try {
      const response = await apiClient.put<ApiResponse<AutopilotSettings>>(
        `${this.basePath}/settings/${projectId}`,
        settings
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to save settings:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to save settings',
      };
    }
  }
  // ===========================================================================
  // Keyword Suggestions
  // ===========================================================================

  /**
   * Get keyword suggestions for a project
   */
  async getKeywordSuggestions(projectId: string, status?: string): Promise<ApiResponse<KeywordSuggestionsResponse>> {
    try {
      const url = status
        ? `${this.basePath}/keywords/${projectId}/suggestions?status=${status}`
        : `${this.basePath}/keywords/${projectId}/suggestions`;
      const response = await apiClient.get<ApiResponse<KeywordSuggestionsResponse>>(url);
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to get keyword suggestions:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch keyword suggestions',
      };
    }
  }

  /**
   * Add a keyword suggestion to tracked keywords
   */
  async addKeywordSuggestion(projectId: string, suggestionId: string, keyword?: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiClient.post<ApiResponse<{ message: string }>>(
        `${this.basePath}/keywords/${projectId}/suggestions/${suggestionId}/add`,
        keyword ? { keyword } : {}
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to add keyword suggestion:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to add keyword',
      };
    }
  }

  /**
   * Dismiss a keyword suggestion
   */
  async dismissKeywordSuggestion(projectId: string, suggestionId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiClient.post<ApiResponse<{ message: string }>>(
        `${this.basePath}/keywords/${projectId}/suggestions/${suggestionId}/dismiss`,
        {}
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to dismiss keyword suggestion:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to dismiss keyword',
      };
    }
  }

  // ===========================================================================
  // Human-Friendly Insights
  // ===========================================================================

  /**
   * Get human-friendly insights for a project
   */
  async getHumanInsights(
    projectId: string,
    options?: { status?: InsightStatus; runId?: string; limit?: number }
  ): Promise<ApiResponse<HumanInsightsResponse>> {
    try {
      const params = new URLSearchParams();
      if (options?.status) params.append('status', options.status);
      if (options?.runId) params.append('runId', options.runId);
      if (options?.limit) params.append('limit', options.limit.toString());

      const queryString = params.toString();
      const url = `${this.basePath}/human-insights/${projectId}${queryString ? `?${queryString}` : ''}`;

      const response = await apiClient.get<ApiResponse<HumanInsightsResponse>>(url);
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to get human insights:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch insights',
      };
    }
  }

  /**
   * Dismiss an insight
   */
  async dismissInsight(insightId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiClient.post<ApiResponse<{ message: string }>>(
        `${this.basePath}/human-insights/${insightId}/dismiss`,
        {}
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to dismiss insight:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to dismiss insight',
      };
    }
  }

  // ===========================================================================
  // Content Prefills
  // ===========================================================================

  /**
   * Create a content prefill for AI Content page
   */
  async createPrefill(request: CreatePrefillRequest): Promise<ApiResponse<CreatePrefillResponse>> {
    try {
      const response = await apiClient.post<ApiResponse<CreatePrefillResponse>>(
        `${this.basePath}/prefills`,
        request
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to create prefill:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create prefill',
      };
    }
  }

  /**
   * Get a content prefill by ID
   */
  async getPrefill(prefillId: string): Promise<ApiResponse<ContentPrefill>> {
    try {
      const response = await apiClient.get<ApiResponse<ContentPrefill>>(
        `${this.basePath}/prefills/${prefillId}`
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to get prefill:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch prefill',
      };
    }
  }

  /**
   * Mark a prefill as used
   */
  async markPrefillUsed(prefillId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiClient.delete<ApiResponse<{ message: string }>>(
        `${this.basePath}/prefills/${prefillId}`
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to mark prefill as used:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to mark prefill as used',
      };
    }
  }

  /**
   * Trigger backfill of insights for recent signals
   */
  async backfillInsights(projectId: string, daysBack = 30): Promise<ApiResponse<{ processed: number; created: number }>> {
    try {
      const response = await apiClient.post<ApiResponse<{ processed: number; created: number }>>(
        `${this.basePath}/backfill/${projectId}`,
        { daysBack }
      );
      return response;
    } catch (error) {
      console.error('[AutopilotService] Failed to backfill insights:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to backfill insights',
      };
    }
  }
}

export const autopilotService = new AutopilotService();
